"use client";

import { useState, useEffect } from "react";
import { useFormik } from "formik";
import {
  Input,
  AuthorAvatar,
  ShareButton,
  FormError,
  FormSuccess,
  brandActionClasses,
} from "@/components/ui";
import CodeBlock from "@/components/ui/code-block";
import { fieldTouchedError } from "@/lib/form-helpers";
import { AlignJustify } from "@/components/icons";
import { TOC_ITEMS, postNewsletterSchema } from "./data";
import { STATS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function StatsCallout() {
  return (
    <div
      className="my-10 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)",
        background:
          "linear-gradient(135deg, var(--surface-1) 0%, color-mix(in srgb, var(--surface-1) 60%, var(--background)) 100%)",
      }}
    >
      <div className="divide-foreground/6 grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {[
          {
            value: STATS.ips,
            label: "Real residential & mobile IPs",
            sub: `across ${STATS.countries} countries`,
          },
          { value: "99.4%", label: "Average success rate", sub: "across all proxy types" },
        ].map((stat) => (
          <div key={stat.value} className="flex flex-col gap-1 px-8 py-7">
            <span className="text-muted-foreground text-4xl font-black tracking-tight">
              {stat.value}
            </span>
            <span className="text-foreground text-sm font-semibold">{stat.label}</span>
            <span className="text-muted-foreground text-xs">{stat.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ArticleChart() {
  const dataPoints = [
    { x: 0, y: 90 },
    { x: 60, y: 85 },
    { x: 120, y: 78 },
    { x: 180, y: 70 },
    { x: 240, y: 55 },
    { x: 300, y: 42 },
    { x: 360, y: 32 },
    { x: 420, y: 22 },
    { x: 480, y: 14 },
    { x: 540, y: 8 },
    { x: 600, y: 4 },
  ];
  const pathD = dataPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = pathD + ` L 600 110 L 0 110 Z`;

  return (
    <div
      className="my-10 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)",
        background: "color-mix(in srgb, var(--surface-1) 43%, var(--background))",
      }}
    >
      <div className="px-6 pt-6 pb-2">
        <p className="text-muted-foreground mb-1 text-xs font-medium tracking-widest uppercase">
          Block Rate Over Time
        </p>
        <p className="text-foreground text-base font-semibold">
          With proper proxy rotation, block rate drops from ~90% to under 5%
        </p>
      </div>
      <div className="px-4 pb-4">
        <svg
          viewBox="-30 -10 660 135"
          className="w-full"
          style={{ height: 180 }}
          aria-label="Line chart showing block rate declining over time with proxy rotation"
        >
          <defs>
            <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="color-mix(in srgb, var(--foreground) 60%, transparent)"
                stopOpacity="0.25"
              />
              <stop
                offset="100%"
                stopColor="color-mix(in srgb, var(--foreground) 60%, transparent)"
                stopOpacity="0"
              />
            </linearGradient>
            <pattern id="chart-grid-v" width="60" height="110" patternUnits="userSpaceOnUse">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="110"
                stroke="var(--foreground)"
                strokeWidth="0.4"
                strokeOpacity="0.06"
              />
            </pattern>
            <pattern id="chart-grid-h" width="600" height="22" patternUnits="userSpaceOnUse">
              <line
                x1="0"
                y1="0"
                x2="600"
                y2="0"
                stroke="var(--foreground)"
                strokeWidth="0.4"
                strokeOpacity="0.06"
              />
            </pattern>
          </defs>
          <rect x="0" y="0" width="600" height="110" fill="url(#chart-grid-v)" />
          <rect x="0" y="0" width="600" height="110" fill="url(#chart-grid-h)" />
          <path d={areaD} fill="url(#chart-fill)" />
          <path
            d={pathD}
            stroke="color-mix(in srgb, var(--foreground) 60%, transparent)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {dataPoints.map((p) => (
            <circle
              key={p.x}
              cx={p.x}
              cy={p.y}
              r="3.5"
              fill="color-mix(in srgb, var(--foreground) 60%, transparent)"
            />
          ))}
          <text
            x="0"
            y="125"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="middle"
          >
            0 req
          </text>
          <text
            x="300"
            y="125"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="middle"
          >
            500 req
          </text>
          <text
            x="600"
            y="125"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="middle"
          >
            1000 req
          </text>
          <text
            x="-8"
            y="94"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="end"
          >
            0%
          </text>
          <text
            x="-8"
            y="50"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="end"
          >
            50%
          </text>
          <text
            x="-8"
            y="9"
            fontSize="9"
            fill="color-mix(in srgb, var(--foreground) 35%, transparent)"
            textAnchor="end"
          >
            100%
          </text>
        </svg>
      </div>
      <div className="px-6 pb-5">
        <p className="text-muted-foreground text-xs italic">
          Fig 1. Block rate vs. request count when using rotating residential proxies across 3
          target domains (30-day test, n=15,000)
        </p>
      </div>
    </div>
  );
}

export { CodeBlock, AuthorAvatar };

export function NewsletterCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: postNewsletterSchema,
    onSubmit: async (values, { resetForm }) => {
      setSubmitError(null);
      try {
        const body = new FormData();
        body.append("email", values.email);
        const res = await fetch("/api/subscribe", { method: "POST", body });
        if (!res.ok) throw new Error("failed");
        setSubmitted(true);
        resetForm();
      } catch {
        setSubmitError("Something went wrong. Please try again.");
      }
    },
  });

  if (submitted) {
    return (
      <section className="my-20" aria-label="Newsletter signup">
        <FormSuccess
          title="You're subscribed!"
          description="Welcome aboard. We'll send the next guide straight to your inbox."
        />
      </section>
    );
  }

  return (
    <section
      className="my-20 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)",
        background:
          "linear-gradient(135deg, var(--surface-1) 0%, color-mix(in srgb, var(--surface-1) 60%, var(--background)) 100%)",
      }}
      aria-label="Newsletter signup"
    >
      <div className="relative overflow-hidden px-8 py-10 sm:px-12 sm:py-12">
        <div
          className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--foreground) 4%, transparent), transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full"
          style={{ background: "radial-gradient(circle, #00e5b310, transparent 70%)" }}
          aria-hidden="true"
        />
        <div className="relative">
          <p className="text-muted-foreground mb-3 text-xs font-semibold">Newsletter</p>
          <h2 className="text-foreground mb-2 text-2xl font-bold sm:text-3xl">Stay in the loop.</h2>
          <p className="text-muted-foreground mb-8 max-w-md text-sm">
            Get the latest guides, proxy infrastructure research, and product updates delivered to
            your inbox — no spam, ever.
          </p>
          <form onSubmit={formik.handleSubmit} className="max-w-md space-y-1" noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="border-foreground/10 bg-foreground/5 h-11 w-full"
                  aria-label="Email address"
                  aria-invalid={!!(formik.touched.email && formik.errors.email)}
                />
                {fieldTouchedError(formik, "email") && (
                  <FormError>{fieldTouchedError(formik, "email")}</FormError>
                )}
                {submitError && <FormError>{submitError}</FormError>}
              </div>
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className={cn(
                  brandActionClasses,
                  "h-11 shrink-0 cursor-pointer rounded-xl px-6 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:self-start"
                )}
              >
                {formik.isSubmitting ? "Subscribing…" : "Subscribe"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export function TableOfContents() {
  const [activeId, setActiveId] = useState<string>(TOC_ITEMS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <aside
      className="sticky top-24 hidden w-52 shrink-0 self-start xl:block"
      aria-label="Table of contents"
    >
      <div className="mb-5 flex items-center gap-2.5">
        <AlignJustify size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground text-sm font-medium">On this page</span>
      </div>
      <nav>
        <ol className="flex flex-col">
          {TOC_ITEMS.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="relative">
                <span
                  className="absolute top-0 bottom-0 left-0 w-px"
                  style={{ background: isActive ? "var(--foreground)" : "var(--border)" }}
                  aria-hidden="true"
                />
                <a
                  href={`#${item.id}`}
                  onClick={() => setActiveId(item.id)}
                  className="block py-2 pl-4 text-sm leading-snug transition-colors duration-150"
                  style={{
                    color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
    </aside>
  );
}

export { ShareButton };
